/* eslint-disable max-len */
import { pad } from './string';
import { has } from './object';


const DAY_NAMES = [
    'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat',
];

const DAY_NAMES_LONG = [
    'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday',
];

const MONTH_NAMES = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const MONTH_NAMES_LONG = [
    'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December',
];


const TOKEN_REGEX = /d{1,4}|m{1,4}|yy(?:yy)?|([HhMsTt])\1?|[LloSZ]|"[^"]*"|'[^']*'/g;
const TIMEZONE_REGEX = /\b(?:[PMCEA][SDP]T|(?:Pacific|Mountain|Central|Eastern|Atlantic) (?:Standard|Daylight|Prevailing) Time|(?:GMT|UTC)(?:[-+]\d{4})?)\b/g;
const TIMEZONE_CLIP_REGEX = /[^\-+\dA-Z]/g;


/*
 * Date Formatting Function
 * Taken From: http://blog.stevenlevithan.com/archives/date-time-format
 * Simplified a bit to get rid of some error protection and alternate call styles
 * this we don't need.
 */
function formatDate(inputDate, mask, utc) {
    // Regexes and supporting functions are cached through closure
    // Passing date through Date applies Date.parse, if necessary
    const date = inputDate ? new Date(inputDate) : new Date();
    if (Number.isNaN(date)) throw SyntaxError('invalid date');

    const _ = utc ? 'getUTC' : 'get';
    const d = date[`${_}Date`]();
    const D = date[`${_}Day`]();
    const m = date[`${_}Month`]();
    const y = date[`${_}FullYear`]();
    const H = date[`${_}Hours`]();
    const M = date[`${_}Minutes`]();
    const s = date[`${_}Seconds`]();
    const L = date[`${_}Milliseconds`]();
    const o = utc ? 0 : date.getTimezoneOffset();
    const flags = {
        d,
        dd: pad(d),
        ddd: DAY_NAMES[D],
        dddd: DAY_NAMES_LONG[D],
        m: m + 1,
        mm: pad(m + 1),
        mmm: MONTH_NAMES[m],
        mmmm: MONTH_NAMES_LONG[m],
        yy: String(y).slice(2),
        yyyy: y,
        h: H % 12 || 12,
        hh: pad(H % 12 || 12),
        H,
        HH: pad(H),
        M,
        MM: pad(M),
        s,
        ss: pad(s),
        l: pad(L, 3),
        L: pad(L > 99 ? Math.round(L / 10) : L),
        t: H < 12 ? 'a' : 'p',
        tt: H < 12 ? 'am' : 'pm',
        T: H < 12 ? 'A' : 'P',
        TT: H < 12 ? 'AM' : 'PM',
        Z: utc ? 'UTC' : (String(date).match(TIMEZONE_REGEX) ||
            ['']).pop().replace(TIMEZONE_CLIP_REGEX, ''),
        o: (o > 0 ? '-' : '+') + pad(Math.floor((Math.abs(o) / 60) * 100) + (Math.abs(o) % 60), 4),
        S: ['th', 'st', 'nd', 'rd'][d % 10 > 3 ? 0 : ((d % 100) - (d % 10) !== 10) * (d % 10)],
    };

    return mask.replace(TOKEN_REGEX, ($0) => {
        return has(flags, $0) ? flags[$0] : $0.slice(1, $0.length - 1);
    });
}

function monthDayTimeFormatter(val) {
    return formatDate(val, 'mmm d HH:MM', true);
}

function monthDayYearFormatter(val) {
    return formatDate(val, "mmm d 'yy", true);
}

function monthYearFormatter(val) {
    return formatDate(val, "mmm 'yy", true);
}

function quarterFormatter(val) {
    // JS month is 0 indexed so we adjust
    const d = new Date(val);
    const month = d.getUTCMonth() + 1;
    let year = d.getUTCFullYear();
    let quarter;
    quarter = '';
    switch (month) {
        case 2:
        case 3:
        case 4:
            quarter = 'Q1';
            break;
        case 5:
        case 6:
        case 7:
            quarter = 'Q2';
            break;
        case 8:
        case 9:
        case 10:
            quarter = 'Q3';
            break;
        case 11:
        case 12:
        case 1:
            quarter = 'Q4';
            if (month === 1) {
                year -= 1;
            }
            break;
        default:
            break;
    }

    const yearDate = new Date(Date.UTC(year, 0));
    const formattedYear = formatDate(yearDate, "'yy", true);
    return `${quarter} ${formattedYear}`;
}

function yearFormatter(val) {
    return formatDate(val, 'yyyy', true);
}


function getFormatter(type, quarterlyFormat) {
    switch (type) {
        case 'intraday':
            return monthDayTimeFormatter;
        case 'daily':
        case 'market_daily':
        case 'weekly':
        case 'biweekly':
            return monthDayYearFormatter;
        case 'monthly':
        case 'bimonthly':
            return monthYearFormatter;
        case 'quarterly':
        case 'semi_yearly':
            if (quarterlyFormat === 'month') return monthYearFormatter;
            return quarterFormatter;
        case 'yearly':
        case 'fiscal_yearly':
            return yearFormatter;
        default:
            return null;
    }
}


export default {
    formatDate,
    getFormatter,
};
